import { api } from '@/lib/api';
import type { WikiDocument, WikiDocumentStatus } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, ArchiveRestore, Eye, MessageSquare, Sparkles } from 'lucide-react';

const STATUS_LABEL: Record<WikiDocumentStatus, string> = {
  uploaded: '업로드됨',
  parsing: '파싱 중',
  parsed: '파싱 완료',
  digesting: 'LLM 정리 중',
  done: '완료',
  failed: '실패',
  deleted: '외부 삭제됨',
  archived: '보관됨',
};

const STATUS_TONE: Record<WikiDocumentStatus, string> = {
  uploaded: 'bg-foreground/10 text-foreground-muted',
  parsing: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  parsed: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  digesting: 'bg-accent/10 text-accent',
  done: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  failed: 'bg-destructive/10 text-destructive',
  deleted: 'bg-foreground/10 text-foreground-faint line-through',
  archived: 'bg-foreground/10 text-foreground-muted',
};

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function isInProgress(s: WikiDocumentStatus) {
  return s === 'uploaded' || s === 'parsing' || s === 'parsed' || s === 'digesting';
}

export function WikiDocuments() {
  const queryClient = useQueryClient();

  const docsQuery = useQuery({
    queryKey: ['wiki', 'documents'],
    queryFn: api.wikiDocuments,
    refetchInterval: (q) => {
      const docs = q.state.data?.documents ?? [];
      return docs.some((d) => isInProgress(d.status)) ? 5_000 : false;
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.wikiDocumentDelete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wiki', 'documents'] }),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.wikiDocumentRestore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wiki', 'documents'] }),
  });

  if (docsQuery.isPending) {
    return <div className="p-8 text-foreground-muted text-sm">로딩 중…</div>;
  }

  const docs = docsQuery.data?.documents ?? [];
  const inProgress = docs.filter((d) => isInProgress(d.status));
  const finished = docs.filter((d) => d.status === 'done');
  const failed = docs.filter((d) => d.status === 'failed');
  const archived = docs.filter((d) => d.status === 'archived');

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <Link
            to="/wikis"
            className="mb-2 inline-block text-xs text-foreground-muted hover:text-accent"
          >
            ← 위키 목차
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">내 자료</h1>
          <p className="mt-1 text-sm text-foreground-muted">
            업로드한 원본 자료 목록 ({docs.length}개) · 진행 중 {inProgress.length} · 완료{' '}
            {finished.length} · 실패 {failed.length}
            {archived.length > 0 ? ` · 보관됨 ${archived.length}` : ''}
          </p>
        </div>
        <Link
          to="/wikis/upload"
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:opacity-90"
        >
          자료 올리기
        </Link>
      </header>

      {inProgress.length > 0 ? (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-foreground-muted">
            처리 중 — 5초마다 자동 갱신
          </h2>
          <ul className="space-y-2">
            {inProgress.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                onArchive={archiveMutation.mutate}
                onRestore={restoreMutation.mutate}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {finished.length > 0 ? (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-foreground-muted">완료된 자료</h2>
          <ul className="space-y-2">
            {finished.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                onArchive={archiveMutation.mutate}
                onRestore={restoreMutation.mutate}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {failed.length > 0 ? (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-destructive">실패</h2>
          <ul className="space-y-2">
            {failed.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                onArchive={archiveMutation.mutate}
                onRestore={restoreMutation.mutate}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {archived.length > 0 ? (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold text-foreground-muted">보관됨</h2>
          <ul className="space-y-2">
            {archived.map((d) => (
              <DocumentRow
                key={d.id}
                doc={d}
                onArchive={archiveMutation.mutate}
                onRestore={restoreMutation.mutate}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {docs.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-8 text-center text-foreground-muted">
          <p className="mb-3">아직 업로드한 자료가 없습니다.</p>
          <Link to="/wikis/upload" className="text-accent underline">
            첫 자료 올리기 →
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function DocumentRow({
  doc,
  onArchive,
  onRestore,
}: {
  doc: WikiDocument;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
}) {
  const isDeleted = doc.status === 'deleted';
  const isArchived = doc.status === 'archived';
  const inactive = isDeleted || isArchived;
  return (
    <li
      className={`rounded-md border bg-surface p-3 ${inactive ? 'border-border opacity-60' : 'border-border'}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        {isDeleted ? (
          <span className="font-medium text-foreground-muted line-through">{doc.originalName}</span>
        ) : isArchived ? (
          <span className="font-medium text-foreground-muted">{doc.originalName}</span>
        ) : (
          <a
            href={api.wikiDocumentDownloadUrl(doc.id)}
            className="font-medium text-foreground hover:text-accent"
          >
            {doc.originalName}
          </a>
        )}
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[doc.status]}`}>
          {STATUS_LABEL[doc.status]}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap gap-3 text-xs text-foreground-faint">
        <span>.{doc.ext}</span>
        <span>{formatBytes(doc.sizeBytes)}</span>
        {doc.categorySlug ? <span>카테고리: {doc.categorySlug}</span> : null}
        <span>{new Date(doc.createdAt).toLocaleString('ko-KR')}</span>
        {doc.lastModifiedByName ? <span>수정: {doc.lastModifiedByName}</span> : null}
      </div>

      {doc.status === 'failed' && doc.error ? (
        <div className="mt-2 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">
          오류: {doc.error}
        </div>
      ) : null}

      {/* 진행 중 안내 */}
      {!inactive && isInProgress(doc.status) ? (
        <div className="mt-2 text-xs text-foreground-muted">
          {doc.status === 'uploaded' && '워커가 처리를 시작합니다…'}
          {doc.status === 'parsing' && '파일을 마크다운으로 변환 중…'}
          {doc.status === 'parsed' && '변환 완료, LLM 이 위키 정리를 시작합니다…'}
          {doc.status === 'digesting' &&
            (doc.digestSummary?.totalChunks ? (
              <span>
                LLM 청크 처리 중 — {doc.digestSummary.processedChunks ?? 0}/
                {doc.digestSummary.totalChunks} 청크
                {doc.digestSummary.currentChunkHeading
                  ? ` · ${doc.digestSummary.currentChunkHeading}`
                  : ''}
                {doc.digestSummary.currentChunkPageRange
                  ? ` (PDF p${doc.digestSummary.currentChunkPageRange})`
                  : ''}
              </span>
            ) : (
              <span>LLM 이 기존 위키와 비교하면서 신규/갱신 페이지를 작성하고 있습니다.</span>
            ))}
        </div>
      ) : null}

      {/* 완료된 자료의 결과 + CTA */}
      {!inactive && doc.status === 'done' ? (
        <>
          {doc.digestSummary?.totalChunks ? (
            <div className="mt-1 text-xs text-foreground-faint">
              {doc.digestSummary.processedChunks}/{doc.digestSummary.totalChunks} 청크 처리
              {doc.digestSummary.truncatedAtChunkCap && doc.digestSummary.allChunks
                ? ` (전체 ${doc.digestSummary.allChunks} 중 cap 적용 — 후반부 미처리)`
                : ''}
            </div>
          ) : null}
          {doc.digestSummary?.pages.length ? (
            <div className="mt-2 text-xs">
              <span className="text-foreground-muted">생성/갱신된 페이지: </span>
              {doc.digestSummary.pages.map((p, i) => (
                <span key={p.slug}>
                  {i > 0 ? ', ' : ''}
                  {doc.externalUrl ? (
                    <a
                      href={doc.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline font-medium"
                    >
                      {p.slug}
                    </a>
                  ) : (
                    <Link
                      to="/wikis/p/$slug"
                      params={{ slug: p.slug }}
                      className="text-accent hover:underline font-medium"
                    >
                      {p.slug}
                    </Link>
                  )}
                  <span className="ml-1 text-foreground-faint">
                    ({p.action === 'created' ? '신규' : '갱신'})
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <div className="mt-2 text-xs text-foreground-muted">
              LLM 이 이 자료에 별도 페이지를 만들지 않았습니다 (기존 페이지로 충분 또는 위키 가치
              낮음).
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {doc.digestSummary?.pages[0] ? (
              doc.externalUrl ? (
                <a
                  href={doc.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground hover:opacity-90"
                >
                  <Sparkles className="h-3 w-3" />
                  원본 페이지 열기
                </a>
              ) : (
                <Link
                  to="/wikis/p/$slug"
                  params={{ slug: doc.digestSummary.pages[0].slug }}
                  className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground hover:opacity-90"
                >
                  <Sparkles className="h-3 w-3" />
                  위키 페이지 보기
                </Link>
              )
            ) : null}
            <Link
              to="/chat"
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent"
            >
              <MessageSquare className="h-3 w-3" />
              챗에서 이 자료 질문
            </Link>
            <Link
              to="/wikis/documents/$id/preview"
              params={{ id: doc.id }}
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent"
            >
              <Eye className="h-3 w-3" />
              변환된 마크다운 보기
            </Link>
            <button
              type="button"
              onClick={() => onArchive(doc.id)}
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-foreground-muted text-foreground-muted"
            >
              <Archive className="h-3 w-3" />
              보관
            </button>
          </div>
        </>
      ) : null}

      {/* 변환된 마크다운 미리보기 (변환 끝났으면 어느 status 든 가능, 비활성 제외) */}
      {!inactive && !isInProgress(doc.status) && doc.status !== 'done' ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Link
            to="/wikis/documents/$id/preview"
            params={{ id: doc.id }}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent"
          >
            <Eye className="h-3 w-3" />
            변환 결과 보기
          </Link>
          <button
            type="button"
            onClick={() => onArchive(doc.id)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-foreground-muted text-foreground-muted"
          >
            <Archive className="h-3 w-3" />
            보관
          </button>
        </div>
      ) : null}

      {/* 진행 중 자료에도 보관 버튼 */}
      {!inactive && isInProgress(doc.status) ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => onArchive(doc.id)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-foreground-muted text-foreground-muted"
          >
            <Archive className="h-3 w-3" />
            보관
          </button>
        </div>
      ) : null}

      {/* 보관됨 → 해제 버튼 */}
      {isArchived ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => onRestore(doc.id)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs hover:border-accent text-foreground-muted"
          >
            <ArchiveRestore className="h-3 w-3" />
            보관 해제
          </button>
        </div>
      ) : null}
    </li>
  );
}
